/**
 * Register F6 — Request More Info, as changed on the 16 August call.
 *
 *   "Routes internally to the originator or underwriter. Editable
 *    missing-fields document, copy and paste out. Not to the broker."
 *
 * The dialog fetches the document, hands it to the operator as editable text,
 * and offers Copy. There is no Send: outbound stays human-driven, and the
 * document says so in its own footer.
 */

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, ClipboardCopy, Loader2, X } from 'lucide-react'

import { ingestionService } from '@/services/ingestion'

export function InfoRequestDialog({
  ingestionId,
  onClose,
}: {
  ingestionId: string
  onClose: () => void
}) {
  const [to, setTo] = useState('')
  const [text, setText] = useState('')
  const [copied, setCopied] = useState(false)

  const req = useQuery({
    queryKey: ['ingestion', ingestionId, 'info-request', to],
    queryFn: () => ingestionService.getInfoRequest(ingestionId, to || undefined),
    enabled: Boolean(ingestionId),
  })

  // Seed the editor from the server once; after that the operator's edits win.
  useEffect(() => {
    if (req.data?.text && !text) setText(req.data.text)
  }, [req.data, text])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function copy() {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      // Clipboard can be refused; the textarea is still selectable by hand.
      setCopied(false)
    }
  }

  const counts = req.data?.counts

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Request more information"
    >
      <div className="flex max-h-[86vh] w-[min(760px,94vw)] flex-col rounded-xl border border-amber-400/20 bg-[#0d0d0d] shadow-2xl">
        <div className="flex items-center justify-between border-b border-amber-400/10 px-4 py-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-stone-500">
              Request more info
            </div>
            <div className="text-[13px] text-stone-200">
              Internal — to the originator or underwriter. Nothing is sent to the broker.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-stone-500 hover:bg-stone-800 hover:text-stone-200"
          >
            <X size={14} />
          </button>
        </div>

        <div className="flex items-center gap-2 border-b border-amber-400/10 px-4 py-2">
          <label className="text-[10px] uppercase tracking-wider text-stone-500" htmlFor="info-req-to">
            To
          </label>
          <input
            id="info-req-to"
            value={to}
            onChange={(e) => {
              setTo(e.target.value)
              setText('') // re-seed from the server with the new addressee
            }}
            placeholder="colleague@yourfirm.com (optional)"
            className="flex-1 rounded border border-stone-700 bg-black/40 px-2 py-1 text-[11px] text-stone-200 placeholder:text-stone-600"
          />
          {counts && (
            <span className="font-mono text-[10px] text-stone-500">
              {counts.missing_required} required · {counts.missing_optional} optional
              {counts.conflicts ? ` · ${counts.conflicts} conflict${counts.conflicts === 1 ? '' : 's'}` : ''}
              {counts.in_unread_model ? ` · ${counts.in_unread_model} in unread model` : ''}
            </span>
          )}
        </div>

        <div className="flex-1 overflow-hidden p-4">
          {req.isLoading && (
            <div className="flex h-40 items-center justify-center gap-2 text-[12px] text-stone-500">
              <Loader2 size={14} className="animate-spin" /> Composing from the record…
            </div>
          )}
          {req.error && (
            <p className="text-[12px] text-red-300">The request could not be composed.</p>
          )}
          {!req.isLoading && !req.error && (
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              spellCheck={false}
              className="h-[52vh] w-full resize-none rounded border border-stone-800 bg-black/40 p-3 font-mono text-[11px] leading-relaxed text-stone-200 focus:border-amber-400/40 focus:outline-none"
            />
          )}
        </div>

        <div className="flex items-center justify-between border-t border-amber-400/10 px-4 py-3">
          <span className="text-[10px] text-stone-600">
            Edit freely, then copy and paste it wherever it needs to go. This never sends.
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-stone-700 px-3 py-1.5 text-[11px] text-stone-400 hover:text-stone-200"
            >
              Close
            </button>
            <button
              type="button"
              onClick={copy}
              disabled={!text}
              className="flex items-center gap-1.5 rounded-lg border border-amber-400/40 px-3 py-1.5 text-[11px] font-medium text-amber-300 hover:bg-amber-400/10 disabled:opacity-50"
            >
              {copied ? <Check size={12} /> : <ClipboardCopy size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InfoRequestDialog
